// End Users Management Page - View/manage users who submit support requests
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Email,
  ConfirmationNumber,
} from '@mui/icons-material';
import { DataTable, type Column } from '@/components/common/DataTable';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useToast } from '@/contexts/ToastContext';
import { ticketsService } from '@/services/collections/tickets';
import { formatRelativeTime } from '@/services/firebase/firestore';
import type { Ticket } from '@/types';

// Aggregated end user data
interface EndUser {
  id: string;
  email: string;
  name?: string;
  ticketCount: number;
  featureRequestCount: number;
  lastActivityAt: Date;
  firstSeenAt: Date;
  status: 'active' | 'blocked';
}

export const EndUsersPage = () => {
  const navigate = useNavigate();
  const { organization, project } = useOrganization();
  const { showError } = useToast();

  const [endUsers, setEndUsers] = useState<EndUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [tab, setTab] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; user: EndUser } | null>(null);

  // Fetch and aggregate end users from tickets
  useEffect(() => {
    const fetchEndUsers = async () => {
      if (!organization || !project) return;

      setLoading(true);
      try {
        // Get all tickets to aggregate end user data
        const tickets = await ticketsService.getByProject(organization.id, project.id, []);

        // Aggregate by email
        const userMap = new Map<string, EndUser>();

        tickets.forEach((ticket: Ticket) => {
          const email = ticket.submitterEmail;
          if (!email) return;

          const existing = userMap.get(email);
          const ticketDate = ticket.createdAt?.toDate?.() || new Date();

          if (existing) {
            existing.ticketCount++;
            if (ticketDate > existing.lastActivityAt) {
              existing.lastActivityAt = ticketDate;
            }
            if (ticketDate < existing.firstSeenAt) {
              existing.firstSeenAt = ticketDate;
            }
            if (ticket.submitterName && !existing.name) {
              existing.name = ticket.submitterName;
            }
          } else {
            userMap.set(email, {
              id: email,
              email,
              name: ticket.submitterName,
              ticketCount: 1,
              featureRequestCount: 0,
              lastActivityAt: ticketDate,
              firstSeenAt: ticketDate,
              status: 'active',
            });
          }
        });

        setEndUsers(Array.from(userMap.values()));
      } catch (error) {
        console.error('Error fetching end users:', error);
        showError('Failed to load end users');
      } finally {
        setLoading(false);
      }
    };

    fetchEndUsers();
  }, [organization, project]);

  // Filter users
  const filteredUsers = endUsers
    .filter((user) => {
      if (tab === 1 && user.status !== 'active') return false;
      if (tab === 2 && user.status !== 'blocked') return false;

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          user.email.toLowerCase().includes(query) ||
          user.name?.toLowerCase().includes(query)
        );
      }
      return true;
    })
    .sort((a, b) => b.lastActivityAt.getTime() - a.lastActivityAt.getTime());

  // Table columns
  const columns: Column<EndUser>[] = [
    {
      id: 'user',
      label: 'User',
      render: (user) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
            {(user.name?.[0] || user.email[0]).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {user.name || 'Unknown'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'tickets',
      label: 'Tickets',
      render: (user) => (
        <Chip
          icon={<ConfirmationNumber sx={{ fontSize: 16 }} />}
          label={user.ticketCount}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      id: 'lastActivity',
      label: 'Last Activity',
      render: (user) => (
        <Typography variant="body2" color="text.secondary">
          {formatRelativeTime(user.lastActivityAt)}
        </Typography>
      ),
    },
    {
      id: 'firstSeen',
      label: 'First Seen',
      render: (user) => (
        <Typography variant="body2" color="text.secondary">
          {formatRelativeTime(user.firstSeenAt)}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (user) => (
        <Chip
          label={user.status}
          size="small"
          color={user.status === 'active' ? 'success' : 'error'}
        />
      ),
    },
    {
      id: 'actions',
      label: '',
      render: (user) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor({ el: e.currentTarget, user });
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // View user's tickets
  const handleViewTickets = (user: EndUser) => {
    navigate(`/app/tickets?email=${encodeURIComponent(user.email)}`);
    setMenuAnchor(null);
  };

  // Send email to user
  const handleSendEmail = (user: EndUser) => {
    window.location.href = `mailto:${user.email}`;
    setMenuAnchor(null);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            End Users
          </Typography>
          <Typography color="text.secondary">
            People who have submitted support requests or feedback
          </Typography>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h4" fontWeight="bold">
              {endUsers.length}
            </Typography>
            <Typography color="text.secondary">Total Users</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h4" fontWeight="bold">
              {endUsers.filter((u) => {
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                return u.lastActivityAt > weekAgo;
              }).length}
            </Typography>
            <Typography color="text.secondary">Active This Week</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h4" fontWeight="bold">
              {endUsers.filter((u) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return u.firstSeenAt >= today;
              }).length}
            </Typography>
            <Typography color="text.secondary">New Today</Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`All (${endUsers.length})`} />
        <Tab label={`Active (${endUsers.filter((u) => u.status === 'active').length})`} />
        <Tab label={`Blocked (${endUsers.filter((u) => u.status === 'blocked').length})`} />
      </Tabs>

      {/* Search */}
      <TextField
        placeholder="Search users by name or email..."
        size="small"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3, width: 300 }}
      />

      {/* Users Table */}
      <DataTable
        columns={columns}
        data={filteredUsers}
        loading={loading}
        onRowClick={(user) => handleViewTickets(user)}
        emptyMessage="No end users found"
      />

      {/* User Menu */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => menuAnchor && handleViewTickets(menuAnchor.user)}>
          <ConfirmationNumber sx={{ mr: 1 }} fontSize="small" /> View Tickets
        </MenuItem>
        <MenuItem onClick={() => menuAnchor && handleSendEmail(menuAnchor.user)}>
          <Email sx={{ mr: 1 }} fontSize="small" /> Send Email
        </MenuItem>
      </Menu>
    </Box>
  );
};
